import { Link } from 'react-router-dom';
import { LandingNav } from './components/LandingNav';
import { LandingFooter } from './components/LandingFooter';

const infoCards = [
  {
    title: 'Account setup',
    body: 'We collect phone numbers and optional display name and language preferences from your customer profile to authenticate the app and remember your settings.',
  },
  {
    title: 'Device identifiers',
    body: 'We store push notification tokens and limited device metadata so optional notifications can reach your device and account sessions stay secure.',
  },
  {
    title: 'Diagnostics',
    body: 'Crashlytics and Sentry capture technical diagnostics, crash details, and limited account or session identifiers when needed to investigate issues and improve reliability.',
  },
];

const usageHighlights = [
  'Authenticate once with Supabase so you can jump straight into coupons and rewards.',
  'Send optional push notifications about shares, new deals, and community updates.',
  'Share only the data necessary to show your wallet, bonuses, and merchant analytics.',
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <LandingNav />

      <main>
        <section className="bg-gradient-to-br from-slate-900 to-sky-900 text-white pt-28 pb-16 md:pt-36">
          <div className="max-w-5xl mx-auto px-6 space-y-6">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-200">Privacy first</p>
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
              Privacy Policy for the Zlinkx Customer App
            </h1>
            <p className="text-sm text-sky-100">Effective date: March 16, 2026</p>
            <p className="text-lg text-slate-100 max-w-3xl">
              Zlinkx helps you discover local coupons, save deals in one wallet, and stay connected with merchants.
              We collect the data needed to operate the app, secure customer accounts, and keep the experience
              reliable. We do not sell your personal information or use app data for cross-app advertising tracking.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold shadow-lg shadow-slate-900/30"
              >
                Contact Support
              </Link>
              <a
                href="https://www.zlinkx.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-white text-white text-sm font-semibold"
              >
                Visit zlinkx.com
              </a>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-16 space-y-10">
          <div className="space-y-4">
            <h2 className="text-3xl font-semibold">Information we collect</h2>
            <p className="text-lg text-slate-600">
              Zlinkx is built to keep data collection narrow and purposeful. These are the main categories of
              information used by the customer app.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {infoCards.map((card) => (
              <div key={card.title} className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6">
                <h3 className="text-xl font-semibold mb-3">{card.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{card.body}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 space-y-4">
            <h3 className="text-2xl font-semibold">How we use your data</h3>
            <ul className="space-y-3 text-slate-600 list-disc list-inside">
              {usageHighlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="text-sm text-slate-500">
              We retain information for as long as needed to operate the service, respond to support requests,
              investigate problems, and comply with legal obligations.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 space-y-5">
              <h3 className="text-2xl font-semibold">Third-party services</h3>
              <p className="text-base text-slate-600">
                We use Supabase for authentication and backend services, Firebase Cloud Messaging for push delivery,
                Firebase Crashlytics for crash reporting, and Sentry for diagnostics and monitoring. These providers
                process only the information required to secure accounts, deliver notifications, and maintain app
                health.
              </p>
              <p className="text-sm text-slate-500">
                We do not use these providers to track you across unrelated apps or websites for advertising purposes.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 space-y-5">
              <h3 className="text-2xl font-semibold">Push notifications</h3>
              <p className="text-base text-slate-600">
                If you opt in, Zlinkx may send notifications about saved offers, new deals, and account-related
                updates. Notification tokens remain associated with your device so those messages can be delivered.
              </p>
              <p className="text-sm text-slate-500">
                You can turn notifications off at any time in your device settings.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 space-y-6">
            <h3 className="text-2xl font-semibold">Your choices</h3>
            <p className="text-base text-slate-600">
              You can update parts of your profile, such as your display name and preferred language, from within the
              app. For privacy questions or requests related to your account data, contact Zlinkx support.
            </p>
            <p className="text-base text-slate-600">
              We do not build advertising profiles, sell personal information, or share identifiable customer data
              with unrelated third parties beyond the service providers listed above.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:text-slate-900 transition-colors"
            >
              Contact privacy support
            </Link>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
